import { getToken } from "./user-service";
import axios from "axios";

/**
 * Get timeline bookings - returns flight dictionary grouped by flight number
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.iata - Airport IATA code
 * @param {string} params.startDate - Start date in YYYY-MM-DD format
 * @param {string} params.endDate - End date in YYYY-MM-DD format
 * @param {boolean} params.rampBookings - Include ramp bookings (optional)
 * @param {string} params.airline - Airline filter (optional)
 * @returns {Promise<Object>} Flight dictionary where keys are flight numbers and values are arrays of bookings
 */
export const getTimelineBookings = async (params) => {
    const token = await getToken();

    // Build query string
    const queryParams = new URLSearchParams({
        iata: params.iata,
        startDate: params.startDate,
        endDate: params.endDate,
    });

    if (params.rampBookings !== undefined) {
        queryParams.append('rampBookings', String(params.rampBookings));
    }

    if (params.airline) {
        queryParams.append('airline', params.airline);
    }

    if (params.boardingBookings !== undefined) {
        queryParams.append('boardingBookings', String(params.boardingBookings));
    }

    if (params.jobsType !== undefined) {
        queryParams.append('jobsType', params.jobsType);
    }

    const url = `/api/v1/bookings/timeline/?${queryParams.toString()}`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await axios.get(url, {
                headers: { "Firebase-UID": token },
            });

            if (response.status >= 400 && response.status < 600) {
                throw new Error("Issue getting timeline bookings");
            }

            resolve(response.data);
        } catch (e) {
            reject(new Error("error getting timeline bookings"));
        }
    });
};

/**
 * Duplicate a booking/task - creates a copy that can be assigned to another worker
 * 
 * @param {Object} params - Duplication parameters
 * @param {string} params.bookingId - The ID of the booking/task to duplicate
 * @returns {Promise<Object>} The created duplicate booking
 */
export const duplicateTask = async (params) => {
    const token = await getToken();
    const url = `/api/v1/bookings/timeline/create-duplicate`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await axios.post(
                url,
                { bookingId: params.bookingId },
                {
                    headers: {
                        "Firebase-UID": token,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status >= 400 && response.status < 600) {
                throw new Error("Issue duplicating task");
            }

            resolve(response.data);
        } catch (e) {
            reject(new Error(e.response?.data?.message || "Error duplicating task"));
        }
    });
};

export const deleteBookingNote = async (params) => {
    const token = await getToken();
    console.log("params", params);
    const url = `/api/v1/bookings/timeline/deleteBookingNote/${encodeURIComponent(params.bookingId)}`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await axios.patch(url, {
                deletedBookingNotes: params.deletedBookingNotes,
                newBookingNotes: params.newBookingNotes,
            }, {
                headers: {
                    "Firebase-UID": token,
                    "Content-Type": "application/json",
                },
            });

            if (response.status >= 400 && response.status < 600) {
                throw new Error("Issue deleting booking note");
            }

            resolve(response.data);
        } catch (e) {
            reject(new Error(e.response?.data?.message || "Error deleting booking note"));
        }
    });
};